import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useOrg } from '../../../shared/contexts/OrgContext';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { useMobile } from '../../../shared/hooks/useMobile';
import Logo from '../../../shared/components/ui/Logo';
import { SearchTopBar } from '../components';
import { supabase } from '../../../shared/lib/supabase';

interface SidebarItem {
  id: string;
  name: string;
  icon: string;
  isActive?: boolean;
}

// Roster App - Blank page with sidebar navigation
const RosterApp: React.FC = () => {
  const { isMobile } = useMobile();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(isMobile);
  const [activeTopNavItem] = useState('for-you');
  const { signOut } = useAuth();
  const { currentOrganization } = useOrg();
  const location = useLocation();
  const navigate = useNavigate();

  const orgIdFromPath = extractOrgIdFromPath(location.pathname);

  // Data state
  const [teamAccess, setTeamAccess] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadTeamData = async () => {
      if (!orgIdFromPath) return;
      
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('team_access')
          .select('id, access_type, granted_at, active, team:team_id(id, name, logo, country, city, venue, website, social_media, date_established, organisation_id)')
          .eq('organisation_id', orgIdFromPath)
          .eq('active', true);

        if (error) {
          console.error('Error fetching team access:', error);
          return;
        }

        setTeamAccess(data || []);
      } catch (error) {
        console.error('Error loading team data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTeamData();
  }, [orgIdFromPath]);
  const isHomePage = location.pathname === `/o/${orgIdFromPath}/roster` || 
                     location.pathname === `/o/${orgIdFromPath}/roster/`;

  const sidebarItems: SidebarItem[] = [
    { id: 'for-you', name: 'Teams', icon: '⭐', isActive: activeTopNavItem === 'for-you' },
    { id: 'players', name: 'Players', icon: '👥', isActive: activeTopNavItem === 'players' },
    { id: 'summary', name: 'Summary', icon: '📊', isActive: activeTopNavItem === 'summary' },
    { id: 'recent', name: 'Recent', icon: '🕒', isActive: activeTopNavItem === 'recent' },
    { id: 'dashboard', name: 'Go to Dashboard', icon: '🏠', isActive: false }
  ];

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch {
      navigate('/');
    }
  };

  const handleLogoClick = () => {
    const actualOrgId = currentOrganization?.id || orgIdFromPath || 'default';
    navigate(`/o/${actualOrgId}`);
  };

  const handleSidebarItemClick = (itemId: string) => {
    if (itemId === 'dashboard') {
      const actualOrgId = currentOrganization?.id || orgIdFromPath || 'default';
      navigate(`/o/${actualOrgId}`);
    } else if (itemId === 'for-you') {
      // Navigate to base roster route
      const actualOrgId = currentOrganization?.id || orgIdFromPath || 'default';
      navigate(`/o/${actualOrgId}/roster`, { replace: true });
    } else if (itemId === 'players') {
      const actualOrgId = currentOrganization?.id || orgIdFromPath || 'default';
      navigate(`/o/${actualOrgId}/roster/players`, { replace: true });
    } else if (itemId === 'summary') {
      // Navigate to summary route
      const actualOrgId = currentOrganization?.id || orgIdFromPath || 'default';
      navigate(`/o/${actualOrgId}/roster/summary`, { replace: true });
    }
  };

  // Categorise teams into organisation-owned ("My Teams") and access-only teams
  const { myTeams, accessTeams } = useMemo(() => {
    if (!orgIdFromPath) {
      return { myTeams: [], accessTeams: [] };
    }

    const ownedTeams = teamAccess.filter(
      (ta) => ta.team?.organisation_id && String(ta.team.organisation_id) === String(orgIdFromPath)
    );
    const ownedTeamIds = new Set(ownedTeams.map((ta) => ta.id));
    const accessibleTeams = teamAccess.filter((ta) => !ownedTeamIds.has(ta.id));

    return { myTeams: ownedTeams, accessTeams: accessibleTeams };
  }, [orgIdFromPath, teamAccess]);

  const filteredMyTeams = useMemo(() => {
    if (!searchTerm.trim()) return myTeams;

    const term = searchTerm.toLowerCase();
    return myTeams.filter((record: any) => {
      const team = record.team;
      return (
        team?.name?.toLowerCase().includes(term) ||
        team?.city?.toLowerCase().includes(term) ||
        team?.country?.toLowerCase().includes(term)
      );
    });
  }, [myTeams, searchTerm]);

  const filteredAccessTeams = useMemo(() => {
    if (!searchTerm.trim()) return accessTeams;

    const term = searchTerm.toLowerCase();
    return accessTeams.filter((record: any) => {
      const team = record.team;
      return (
        team?.name?.toLowerCase().includes(term) ||
        team?.city?.toLowerCase().includes(term) ||
        team?.country?.toLowerCase().includes(term)
      );
    });
  }, [accessTeams, searchTerm]);

  const hasAnyTeams = myTeams.length > 0 || accessTeams.length > 0;
  const hasTeams = filteredMyTeams.length > 0 || filteredAccessTeams.length > 0;

  // Render helper for a single team card
  const renderTeamCard = (ta: any) => (
    <div 
      key={ta.id} 
      className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200 cursor-pointer"
      onClick={() => navigate(`/o/${orgIdFromPath}/roster/${ta.team?.id}`)}
    >
      {/* Team Logo Header */}
      <div className="h-32 bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        {ta.team?.logo ? (
          <img 
            src={ta.team.logo} 
            alt={ta.team.name} 
            className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg" 
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-white border-4 border-white shadow-lg flex items-center justify-center">
            <span className="text-2xl">⚽</span>
          </div>
        )}
      </div>

      {/* Team Details */}
      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">{ta.team?.name || 'Unknown Team'}</h3>
        <p className="text-sm text-gray-600 mb-3">
          {ta.team?.city || '—'}, {ta.team?.country || '—'}
        </p>

        {/* Team Info - Always show all labels for consistent layout */}
        <div className="space-y-2 mb-4">
          {/* Venue - Always show */}
          <div className="flex items-center text-sm text-gray-600">
            <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span className="truncate">{ta.team?.venue || '—'}</span>
          </div>
          
          {/* Established - Always show */}
          <div className="flex items-center text-sm text-gray-600">
            <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>
              Est. {ta.team?.date_established 
                ? new Date(ta.team.date_established).getFullYear() 
                : '—'}
            </span>
          </div>
        </div>

        {/* Website Link - Always show text, only clickable if website exists */}
        <div className="border-t border-gray-100 pt-3">
          {ta.team?.website ? (
            <a 
              href={ta.team.website} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Visit Website
            </a>
          ) : (
            <div className="flex items-center text-sm text-gray-400">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Visit Website
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderTeamSection = (title: string, teams: any[]) => {
    if (teams.length === 0) return null;

    return (
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            {teams.length} {teams.length === 1 ? 'team' : 'teams'}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {teams.map((teamAccessRecord: any) => renderTeamCard(teamAccessRecord))}
        </div>
      </section>
    );
  };

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Mobile Overlay */}
      {isMobile && isHomePage && !isSidebarCollapsed && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsSidebarCollapsed(true)}
        />
      )}

      {/* Custom Sidebar - Only on home route */}
      {isHomePage && (
        <div className={`bg-white border-r border-gray-200 transition-all duration-300 flex-shrink-0 relative flex flex-col min-h-0 ${
          isMobile 
            ? `fixed inset-y-0 left-0 z-50 w-64 ${isSidebarCollapsed ? '-translate-x-full' : 'translate-x-0'}`
            : isSidebarCollapsed ? 'w-16' : 'w-64'
        }`}>
          <div className="p-3 sm:p-4 flex flex-col h-full min-h-0">
            {/* Logo - Now Clickable */}
            <div className="flex items-center justify-start mb-3 sm:mb-4 flex-shrink-0">
              <button
                onClick={handleLogoClick}
                className="flex items-center space-x-2 hover:opacity-80 transition-opacity duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md p-1 min-h-44 min-w-44"
                title="Go to Homepage"
                aria-label="Go to Homepage"
              >
              <Logo 
                size="md" 
                variant={isSidebarCollapsed ? 'mark' : 'full'} 
                className="flex items-center space-x-2"
              />
              </button>
            </div>

            {/* Expand/Collapse Button - Better z-index for clickability */}
            <div className="absolute top-3 sm:top-4 right-0 transform translate-x-1/2 z-50 pointer-events-auto">
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="w-6 h-6 bg-white border border-gray-200 rounded-full shadow-sm flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors min-h-44 min-w-44"
                title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                aria-label={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isSidebarCollapsed ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  )}
                </svg>
              </button>
            </div>

            {/* Main Section - scrollable when content overflows */}
            <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="mb-1">
              <div className="space-y-1 px-2 sm:px-3">
                {sidebarItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleSidebarItemClick(item.id)}
                    className={`w-full flex items-center ${
                      isSidebarCollapsed 
                        ? 'justify-center px-2 py-1.5' 
                        : 'space-x-2 sm:space-x-3 px-2 sm:px-3 py-1.5'
                    } rounded-lg transition-colors min-h-44 ${
                      item.isActive 
                        ? 'bg-blue-50 text-blue-700' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-sm">{item.icon}</span>
                    {!isSidebarCollapsed && (
                      <span className="text-xs sm:text-sm font-normal">{item.name}</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Access Tables Section */}
              {!isSidebarCollapsed && (
                <>
                  <div className="my-4 border-t border-gray-200" />
                  <div className="mb-2 px-2 text-[11px] uppercase tracking-wide text-gray-500">Access tables</div>
                  <div className="space-y-1 px-2 sm:px-3">
                    {[
                      { id: 'tournament-access', name: 'Tournament Access', icon: '🏆' },
                      { id: 'team-access', name: 'Team Access', icon: '🧑‍🤝‍🧑' },
                      { id: 'season-access', name: 'Season Access', icon: '🗂️' },
                      { id: 'player-access', name: 'Player Access', icon: '👤' }
                    ].map((t) => (
                      <button
                        key={t.id}
                        onClick={() => navigate(`/o/${orgIdFromPath}/roster/summary#${t.id}`)}
                        className={`w-full flex items-center ${
                          isSidebarCollapsed 
                            ? 'justify-center px-2 py-1.5' 
                            : 'space-x-2 px-2 sm:px-3 py-1.5'
                        } rounded-lg text-gray-600 hover:bg-gray-50 text-[13px]`}
                      >
                        <span className="text-sm">{t.icon}</span>
                        {!isSidebarCollapsed && (
                          <span className="font-normal">{t.name}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            </div>

            {/* Logout Button - Sticks to bottom */}
            <div className="mt-auto pt-2 sm:pt-3 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={handleLogout}
                className={`w-full flex items-center ${
                  isSidebarCollapsed 
                    ? 'justify-center px-2 py-1.5' 
                    : 'space-x-2 sm:space-x-3 px-2 sm:px-3 py-1.5'
                } text-gray-700 hover:bg-gray-50 rounded-lg transition-colors min-h-44`}
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {!isSidebarCollapsed && (
                  <span className="text-xs sm:text-sm font-normal">Logout</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar with menu toggle */}
        {isMobile && isHomePage && (
          <div className="sticky top-0 z-40 bg-white border-b border-gray-200 px-3 py-2 flex items-center">
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Toggle menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="ml-2 font-medium text-gray-900">Teams</div>
          </div>
        )}
        <main className="flex-1 overflow-y-auto">
          <div className={`${isHomePage && isSidebarCollapsed ? 'px-4 py-6' : 'p-6'}`}>
            <SearchTopBar
              title="Teams"
              placeholder="Search teams..."
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
            />

            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading teams...</span>
              </div>
            ) : hasTeams ? (
              <div className="space-y-10">
                {renderTeamSection('My Teams', filteredMyTeams)}
                {renderTeamSection('Access Teams', filteredAccessTeams)}
              </div>
            ) : hasAnyTeams ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No matches found</h3>
                <p className="text-sm text-gray-500">Try a different search term.</p>
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Teams Found</h3>
                <p className="text-sm text-gray-500">You don't have access to any teams yet.</p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default RosterApp;